import { getJWTToken } from '../../utils/auth';
import { authHeaders } from '../../core/impersonation';
import type {
  Cell,
  GrowthClass,
  GrowthFigures,
  GrowthHistoryYear,
  GrowthLevel,
  GrowthPage,
  GrowthPeriod,
  GrowthResponse,
  GrowthRow,
  GrowthRubric,
  GrowthStudent,
  GrowthStudentRow,
  GrowthTile,
  ReturnTesting,
  SchemeLevel,
  Skill,
  SkillRow,
  SkillTile,
} from './types';
import { SKILLS } from './types';

const apiUrl = import.meta.env.REACT_APP_BAMBOO_API_URL ?? '';

export class GrowthNotFound extends Error {
  constructor() {
    super('growth: not found');
    this.name = 'GrowthNotFound';
  }
}

export interface GrowthQuery {
  level: GrowthLevel;
  grade?: string;
  school?: string;
  teacher?: string;
  year?: number;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function num(value: unknown, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function numOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function str(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function strings(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
}

function asPeriod(value: unknown): GrowthPeriod | null {
  if (!isRecord(value)) return null;
  return {
    schoolYear: num(value.schoolYear),
    label: str(value.label),
    later: value.later === 'eoy' ? 'eoy' : 'moy',
    lastYear: value.lastYear === true,
    open: value.open === true,
    growthLabel: str(value.growthLabel, 'On track'),
  };
}

function asSkillTile(value: unknown): SkillTile {
  const v = isRecord(value) ? value : {};
  return { matched: num(v.matched), onTrack: num(v.onTrack) };
}

function asScheme(value: unknown): SchemeLevel[] {
  if (!Array.isArray(value)) return [];
  return value.filter(isRecord).map((level) => ({
    score: num(level.score),
    label: str(level.label),
    color: str(level.color),
    description: str(level.description),
  }));
}

function asNumbers(value: unknown, length: number): number[] {
  const out = Array.isArray(value) ? value.map((v) => num(v)) : [];
  while (out.length < length) out.push(0);
  return out.slice(0, length);
}

function asReturnTesting(value: unknown): ReturnTesting {
  const v = isRecord(value) ? value : {};
  return { total: num(v.total), matched: num(v.matched), noBoy: num(v.noBoy), noLater: num(v.noLater), unscorable: num(v.unscorable) };
}

function asFigures(value: unknown): GrowthFigures {
  const v = isRecord(value) ? value : {};
  const grid = Array.isArray(v.grid) ? v.grid.map((row) => asNumbers(row, 5)) : [];
  while (grid.length < 5) grid.push([0, 0, 0, 0, 0]);
  return {
    students: num(v.students),
    matched: num(v.matched),
    levels: asNumbers(v.levels, 5),
    avg: numOrNull(v.avg),
    boyAvg: numOrNull(v.boyAvg),
    growth: numOrNull(v.growth),
    onTrack: num(v.onTrack),
    bridgingPlus: num(v.bridgingPlus),
    grid: grid.slice(0, 5),
    returnTesting: asReturnTesting(v.returnTesting),
    tested: num(v.tested),
    words: numOrNull(v.words),
    boyWords: numOrNull(v.boyWords),
  };
}

function asSkillFigures(value: unknown): Record<Skill, GrowthFigures> {
  const v = isRecord(value) ? value : {};
  return { speaking: asFigures(v.speaking), writing: asFigures(v.writing) };
}

function asRow(value: unknown): GrowthRow {
  const v = isRecord(value) ? value : {};
  const row: GrowthRow = {
    key: str(v.key),
    label: str(v.label),
    sub: str(v.sub),
    students: num(v.students),
    teachers: num(v.teachers),
    shared: num(v.shared),
    drillable: v.drillable === true,
    skills: asSkillFigures(v.skills),
  };
  if (typeof v.assignments === 'number') row.assignments = v.assignments;
  return row;
}

function asRows(value: unknown): GrowthRow[] {
  return Array.isArray(value) ? value.map(asRow) : [];
}

function asCell(value: unknown): Cell {
  const v = isRecord(value) ? value : {};
  const state = v.state;
  const cell: Cell = { state: state === 'scored' || state === 'unscorable' || state === 'submitted' ? state : 'none' };
  if (typeof v.band === 'number') cell.band = v.band;
  if (typeof v.label === 'string') cell.label = v.label;
  if (typeof v.color === 'string' && v.color !== '') cell.color = v.color;
  if (typeof v.value === 'number') cell.value = v.value;
  return cell;
}

function asSkillRow(value: unknown): SkillRow {
  const v = isRecord(value) ? value : {};
  return { boy: asCell(v.boy), moy: asCell(v.moy), eoy: asCell(v.eoy), change: numOrNull(v.change), onTrack: v.onTrack === true };
}

function asStudentRow(value: unknown): GrowthStudentRow {
  const v = isRecord(value) ? value : {};
  return {
    id: str(v.id),
    name: str(v.name),
    tags: strings(v.tags),
    also: strings(v.also),
    classes: strings(v.classes),
    speaking: asSkillRow(v.speaking),
    writing: asSkillRow(v.writing),
    status: str(v.status, 'Not started'),
    hasLater: v.hasLater === true,
  };
}

function asClass(value: unknown): GrowthClass {
  const v = isRecord(value) ? value : {};
  return { id: str(v.id), name: str(v.name), period: numOrNull(v.period), students: num(v.students) };
}

function asPage(body: unknown): GrowthPage {
  if (!isRecord(body)) throw new Error('growth response is empty');
  const level = String(body.level);
  if (!['district', 'grade', 'school', 'teacher'].includes(level)) throw new Error('growth response has an unknown level');
  if (!isRecord(body.totals)) throw new Error('growth response has no totals');
  const page: GrowthPage = {
    level: level as GrowthLevel,
    period: asPeriod(body.period),
    dataAsOf: str(body.dataAsOf),
    district: str(body.district),
    title: str(body.title),
    subtitle: str(body.subtitle),
    students: num(body.students),
    years: Array.isArray(body.years) ? body.years.map((y) => num(y)).filter((y) => y > 0) : [],
    currentYear: num(body.currentYear),
    scheme: asScheme(body.scheme),
    totals: asSkillFigures(body.totals),
    byGrade: asRows(body.byGrade),
    byBand: asRows(body.byBand),
    bySchool: 'bySchool' in body ? asRows(body.bySchool) : null,
    groups: asRows(body.groups),
    classes: Array.isArray(body.classes) ? body.classes.map(asClass) : [],
    studentRows: Array.isArray(body.studentRows) ? body.studentRows.map(asStudentRow) : [],
  };
  if (typeof body.grade === 'string' && body.grade) page.grade = body.grade;
  if (typeof body.school === 'string' && body.school) page.school = body.school;
  if (typeof body.teacher === 'string' && body.teacher) page.teacher = body.teacher;
  if (typeof body.summary === 'string' && body.summary) page.summary = body.summary;
  return page;
}

function asTile(body: unknown): GrowthTile {
  if (!isRecord(body)) throw new Error('growth tile response is empty');
  if (!isRecord(body.headline)) throw new Error('growth tile response has no headline');
  const skills: Partial<Record<Skill, SkillTile>> = {};
  if (isRecord(body.skills)) {
    for (const skill of SKILLS) {
      if (isRecord(body.skills[skill])) skills[skill] = asSkillTile(body.skills[skill]);
    }
  }
  const tile: GrowthTile = {
    period: asPeriod(body.period),
    dataAsOf: str(body.dataAsOf),
    headline: asSkillTile(body.headline),
    skills,
    students: num(body.students),
    tested: num(body.tested),
  };
  if (typeof body.opensAt === 'string' && body.opensAt) tile.opensAt = body.opensAt;
  if (body.opensWindow === 'boy' || body.opensWindow === 'moy' || body.opensWindow === 'eoy') tile.opensWindow = body.opensWindow;
  if (typeof body.nextStartsAt === 'string' && body.nextStartsAt) tile.nextStartsAt = body.nextStartsAt;
  if (Array.isArray(body.years)) tile.years = body.years.map((y) => num(y)).filter((y) => y > 0);
  return tile;
}

function asRubric(value: unknown): GrowthRubric | undefined {
  if (!isRecord(value)) return undefined;
  return {
    later: value.later === 'eoy' ? 'eoy' : 'moy',
    indicators: Array.isArray(value.indicators)
      ? value.indicators.filter(isRecord).map((i) => ({ name: str(i.name), now: num(i.now), was: numOrNull(i.was) }))
      : [],
    scoredBy: str(value.scoredBy),
    scoredAt: str(value.scoredAt),
    gradedBy: str(value.gradedBy),
  };
}

function asResponse(value: unknown): GrowthResponse {
  const v = isRecord(value) ? value : {};
  const kind = v.kind === 'boy' || v.kind === 'eoy' ? v.kind : 'moy';
  const response: GrowthResponse = {
    window: str(v.window),
    schoolYear: num(v.schoolYear),
    kind,
    skill: v.skill === 'writing' ? 'writing' : 'speaking',
    band: num(v.band),
    label: str(v.label),
    ...(typeof v.color === 'string' && v.color !== '' ? { color: v.color } : {}),
    durationSec: numOrNull(v.durationSec),
    wordCount: numOrNull(v.wordCount),
    boyDurationSec: numOrNull(v.boyDurationSec),
    boyWordCount: numOrNull(v.boyWordCount),
  };
  if (typeof v.listenUrl === 'string' && v.listenUrl) response.listenUrl = v.listenUrl;
  if (typeof v.text === 'string' && v.text) response.text = v.text;
  return response;
}

function asHistoryYear(value: unknown): GrowthHistoryYear {
  const v = isRecord(value) ? value : {};
  return { schoolYear: num(v.schoolYear), label: str(v.label), speaking: asSkillRow(v.speaking), writing: asSkillRow(v.writing) };
}

function asStudent(body: unknown): GrowthStudent {
  if (!isRecord(body) || typeof body.id !== 'string') throw new Error('growth student response is empty');
  const rubric: Partial<Record<Skill, GrowthRubric>> = {};
  if (isRecord(body.rubric)) {
    for (const skill of SKILLS) {
      const parsed = asRubric(body.rubric[skill]);
      if (parsed) rubric[skill] = parsed;
    }
  }
  return {
    id: body.id,
    name: str(body.name),
    grade: str(body.grade),
    school: str(body.school),
    teachers: strings(body.teachers),
    period: asPeriod(body.period),
    dataAsOf: str(body.dataAsOf),
    planRoute: str(body.planRoute),
    scheme: asScheme(body.scheme),
    history: Array.isArray(body.history) ? body.history.map(asHistoryYear) : [],
    rubric,
    responses: Array.isArray(body.responses) ? body.responses.map(asResponse) : [],
  };
}

async function readGrowth(path: string): Promise<unknown> {
  const token = await getJWTToken();
  const response = await fetch(`${apiUrl}${path}`, { headers: authHeaders(token) });
  if (response.status === 404) throw new GrowthNotFound();
  if (!response.ok) throw new Error(`growth request failed: ${response.status}`);
  return response.json();
}

export function growthPath(query: GrowthQuery): string {
  const params = new URLSearchParams();
  let path = '/growth';
  switch (query.level) {
    case 'grade':
      path = `/growth/grades/${encodeURIComponent(query.grade ?? '')}`;
      break;
    case 'school':
      path = `/growth/schools/${encodeURIComponent(query.school ?? '')}`;
      if (query.grade) params.set('grade', query.grade);
      break;
    case 'teacher':
      path = `/growth/teachers/${encodeURIComponent(query.teacher ?? '')}`;
      if (query.school) params.set('school', query.school);
      break;
    default:
      break;
  }
  if (query.year) params.set('year', String(query.year));
  const encoded = params.toString();
  return encoded ? `${path}?${encoded}` : path;
}

export async function fetchGrowthTile(): Promise<GrowthTile> {
  return asTile(await readGrowth('/growth/tile'));
}

export async function fetchGrowthPage(query: GrowthQuery): Promise<GrowthPage> {
  return asPage(await readGrowth(growthPath(query)));
}

export async function fetchGrowthStudent(studentId: string, year?: number): Promise<GrowthStudent> {
  const suffix = year ? `?year=${year}` : '';
  return asStudent(await readGrowth(`/growth/students/${encodeURIComponent(studentId)}${suffix}`));
}
